const API_BASE = process.env.FIELDFORCE_API_BASE || process.env.VITE_FIELDFORCE_API_BASE;
const API_KEY = process.env.FIELDFORCE_API_KEY || process.env.VITE_FIELDFORCE_API_KEY;

interface FetchOptions {
  method?: 'GET' | 'POST';
  body?: any;
}

let globalToken: string | null = null;
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

export async function loginFieldforce(): Promise<string> {
  if (isRefreshing && refreshPromise) return refreshPromise;
  
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const body = {
        UserName: process.env.FIELDFORCE_USERNAME || process.env.VITE_FIELDFORCE_USERNAME || '',
        Password: process.env.FIELDFORCE_PASSWORD || process.env.VITE_FIELDFORCE_PASSWORD || ''
      };
      const response = await fetch(`${API_BASE}/authenticate`, {
        method: 'POST',
        headers: {
          'x-api-key': API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      const resData = await response.json();
      
      if (!response.ok || !resData.data?.access_token) {
        throw new Error(resData.error || resData.message || 'Authentication failed');
      }
      
      globalToken = resData.data.access_token;
      return globalToken;
    } catch (err) {
      console.error('[Fieldforce] Auth error:', err);
      throw err;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
}

async function fetchFieldforce(endpoint: string, options: FetchOptions = {}, retryCount = 0): Promise<any> {
  const url = `${API_BASE}${endpoint}`;
  
  // Ensure we have a token before proceeding
  if (!globalToken) {
    await loginFieldforce();
  }
  
  const doFetch = async (token: string) => {
    const response = await fetch(url, {
      method: options.method || 'POST',
      headers: {
        'x-api-key': API_KEY,
        'access-token': token,
        'Content-Type': 'application/json',
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    const data = await response.json();

    // Check for 401 or token-related errors in the response body
    const isUnauthorized = response.status === 401 || (data && (
      String(data.error || '').toLowerCase().includes('token') || 
      String(data.message || '').toLowerCase().includes('token') ||
      String(data.error || '').toLowerCase().includes('unauthorized')
    ));

    if (isUnauthorized) {
      if (retryCount < 1) {
        console.warn('[Fieldforce] Token expired, refreshing globally...');
        await loginFieldforce();
        return fetchFieldforce(endpoint, options, retryCount + 1);
      }
    }

    if (!response.ok || (data && (data.code >= 400 || data.error))) {
      const errorDetail = typeof data.error === 'object' ? JSON.stringify(data.error) : (data.message || data.error || response.statusText);
      throw new Error(`Fieldforce API error: ${errorDetail}`);
    }

    return data;
  };

  return doFetch(globalToken!);
}

export async function getBranches(page = 1, pageSize = 250) {
  return fetchFieldforce('/branch/GetBranches', {
    body: {
      active: 'all',
      num_rows: pageSize,
      page: page
    }
  });
}

export async function saveBranch(branchData: any) {
  return fetchFieldforce('/branch/SaveBranch', {
    body: {
      data: [branchData]
    }
  });
}

export async function getFieldforceProducts(branchName: string, page = 1, pageSize = 250) {
  return fetchFieldforce('/Product/GetProducts', {
    body: {
      branch_name: branchName,
      num_rows: pageSize,
      page: page
    }
  });
}

export async function getWarehouseSnapshot(branchName: string, page: number = 1) {
  return fetchFieldforce('/StockSnapshot/WarehouseSnapshot', {
    body: {
      branch_name: branchName,
      page: page,
      num_rows: 250
    }
  });
}

export async function getCompletedOrders(startDate: string, endDate: string, page: number = 1, numRows: number = 200) {
  return fetchFieldforce('/PlantOrders/GetCompletedOrders', {
    body: {
      start_date: startDate,
      end_date: endDate,
      num_rows: numRows,
      page: page
    }
  });
}

export async function getDeliveredSales(startDate: string, endDate: string, page: number = 1, numRows: number = 200) {
  return fetchFieldforce('/Sales/DeliveredSales', {
    method: 'POST',
    body: {
      start_date: startDate,
      end_date: endDate,
      num_rows: numRows,
      page: page,
      order_status: 'delivered',
      date_type: 'delivered'
    }
  });
}
